import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { BankOutlined, WalletOutlined } from '@ant-design/icons';
import { Button, Switch } from 'antd';
import { useDispatch, useSelector } from 'react-redux';

import { formatters } from 'services';
import ETFDetailsModal from 'reusable/ETFDetailsModal';
import {
  CREATING,
  fetchBlotter,
  findAFSBondMarketReplacementInstrument,
  FinancialInstrument,
  FROZEN,
  instrumentTypes,
  PAUSED,
  selectors,
  SESSION_STATUSES,
  utils,
} from 'state';
import { fetchClientMoney } from '../../../state/app/client_money/actions';
import { fetchOptionsChains } from '../../../state/app/options/actions';

import ExchangeModal from '../MarketsTab/ExchangeModal';
import PortfolioOverview from '../MarketsTab/PortfolioOverview';
import ODAModal from '../ODAModal';
import OrderModal from '../OrderModal';
import SimplifiedOptionOrderModal from '../SimplifiedOptions/SimplifiedOptionOrderModal';
import { TraineeSessionContext } from '../TraineeSessionContext';
import BondsMarket from './BondsMarket';
import ExperienceToggle from './ExperienceToggle';
import MarketSection from './MarketSection';
import OptionsMarket from './OptionsMarket';
import SpotMarkets from './SpotMarkets';
import {
  groupSession23Markets,
  instrumentHasPosition,
  ordinaryQuoteIsAvailable,
  sessionBlocksTrading,
} from './model';
import type { MarketsExperience } from './model';
import type { MarketInstrument } from './types';

type Props = {
  experience: MarketsExperience;
  onExperienceChange: (experience: MarketsExperience) => void;
};

const scrollToSection = (id: string) => {
  document.getElementById(id)?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
};

const EmptyPositions: React.FC = () => (
  <div className="markets-v2-empty">
    No open position in this market. Turn off <b>Positions only</b> to restore
    the complete universe.
  </div>
);

const MarketBoard: React.FC<Props> = ({ experience, onExperienceChange }) => {
  const dispatch = useDispatch();
  const traineeContext = useContext(TraineeSessionContext);
  const session = useSelector(selectors.activeSession);
  const player = useSelector(selectors.player);
  const assets = useSelector(selectors.underlyingAssets);
  const assetList = useSelector(selectors.underlyingAssetsList);
  const currencies = useSelector(selectors.currencies);
  const currencyList = useSelector(selectors.currenciesList);
  const marketHours = useSelector(selectors.marketHours);
  const instruments = useSelector(
    selectors.playerMarketFinancialInstrumentsList,
  );
  const wallets = useSelector(selectors.wallets);
  const orders = useSelector(selectors.orders);
  const clientsFetched = useSelector(selectors.clientsFetched);
  const hasLegacyOptions = useSelector(selectors.sessionHasOptions);
  const { fetched_blotter: blotterFetched } = player;
  const reportingCurrency = currencies[session.reporting_currency];
  const reportingCurrencySymbol = reportingCurrency?.symbol || '';
  const playableSymbols = useSelector(
    selectors.playableCurrencies(instruments),
  );

  const [positionsOnly, setPositionsOnly] = useState(false);
  const [selectedInstrument, setSelectedInstrument] = useState<
    FinancialInstrument | undefined
  >();
  const [visibleOrderModal, setVisibleOrderModal] = useState(false);
  const [visibleOptionModal, setVisibleOptionModal] = useState(false);
  const [visibleODAModal, setVisibleODAModal] = useState(false);
  const [visibleExchangeModal, setVisibleExchangeModal] = useState(false);
  const [detailsAssetId, setDetailsAssetId] = useState<number>();

  useEffect(() => {
    if (!blotterFetched) dispatch(fetchBlotter({}));
  }, [blotterFetched, dispatch]);

  useEffect(() => {
    if (session.client_money && !clientsFetched) {
      dispatch(fetchClientMoney({ session_id: session.id }));
    }
    if (hasLegacyOptions) {
      dispatch(fetchOptionsChains({ session_id: session.id }));
    }
  }, [
    clientsFetched,
    dispatch,
    hasLegacyOptions,
    session.client_money,
    session.id,
  ]);

  const groups = useMemo(
    () => groupSession23Markets(instruments, assets),
    [assets, instruments],
  );

  const rowsByInstrumentId = useMemo(() => {
    const rows = new Map<number, MarketInstrument>();
    instruments.forEach(instrument => {
      const asset = assets[instrument.underlying_asset];
      const currency = asset && currencies[asset.base_currency];
      if (!asset || !currency || !reportingCurrency) return;
      const [marketIsOpen, closedFromInterval] = utils.assetIsOpenNew(
        asset,
        session,
        marketHours[asset.market_hours],
      );
      rows.set(instrument.id, {
        instrument,
        asset,
        currencySymbol: currency.symbol,
        reportingValue: utils.reportingValue(
          instrument,
          currency,
          asset.resourcetype,
          assetList,
          currencyList,
          reportingCurrency,
        ),
        marketIsOpen,
        closedFromInterval,
      });
    });
    return rows;
  }, [
    assetList,
    assets,
    currencies,
    currencyList,
    instruments,
    marketHours,
    reportingCurrency,
    session,
  ]);

  const rowsFor = useCallback(
    (items: FinancialInstrument[]) =>
      items
        .filter(item => !positionsOnly || instrumentHasPosition(item))
        .map(item => rowsByInstrumentId.get(item.id))
        .filter((row): row is MarketInstrument => Boolean(row)),
    [positionsOnly, rowsByInstrumentId],
  );

  const fxRows = useMemo(
    () =>
      rowsFor(groups.fx).sort((a, b) =>
        (a.asset.name || a.asset.ticker).localeCompare(
          b.asset.name || b.asset.ticker,
        ),
      ),
    [groups.fx, rowsFor],
  );
  const etfRows = useMemo(() => rowsFor(groups.etfs), [groups.etfs, rowsFor]);
  const optionRows = useMemo(
    () => rowsFor(groups.options),
    [groups.options, rowsFor],
  );
  const bondRows = useMemo(
    () =>
      rowsFor(groups.bonds).sort((a, b) =>
        (a.asset.asset_issue_date || '').localeCompare(
          b.asset.asset_issue_date || '',
        ),
      ),
    [groups.bonds, rowsFor],
  );

  const selectInstrument = useCallback(
    (instrument: FinancialInstrument) => {
      const asset = assets[instrument.underlying_asset];
      const typeIndex = instrumentTypes.findIndex(
        item => item.type === asset?.resourcetype,
      );
      if (typeIndex >= 0) traineeContext.setInstrumentType(`${typeIndex}`);
      traineeContext.setShowCash(false);
      setSelectedInstrument(instrument);
    },
    [assets, traineeContext],
  );

  useEffect(() => {
    const currentStillExists = instruments.some(
      instrument => instrument.id === selectedInstrument?.id,
    );
    if (currentStillExists) return;
    const replacement = findAFSBondMarketReplacementInstrument(
      selectedInstrument,
      instruments,
      session.afs_bond_market_replacements,
    );
    if (replacement) {
      setVisibleOrderModal(false);
      setVisibleODAModal(false);
      selectInstrument(replacement);
      return;
    }
    const preferred =
      groups.fx[0] || groups.etfs[0] || groups.bonds[0] || groups.options[0];
    if (preferred) selectInstrument(preferred);
  }, [
    groups,
    instruments,
    selectInstrument,
    selectedInstrument,
    session.afs_bond_market_replacements,
  ]);

  const configuredCount =
    groups.fx.length +
    groups.etfs.length +
    groups.options.length +
    groups.bonds.length;
  const activePositions = instruments.filter(instrumentHasPosition).length;
  const availableQuotes = Array.from(rowsByInstrumentId.values()).filter(row =>
    row.asset.resourcetype === 'SimplifiedOption'
      ? row.instrument.simplified_option?.availability?.available === true
      : ordinaryQuoteIsAvailable(row.asset),
  ).length;

  const playableWallets = wallets.filter(wallet =>
    playableSymbols.includes(currencies[wallet.currency]?.symbol),
  );
  const etfResourceTypes = new Set(
    groups.etfs.map(item => assets[item.underlying_asset]?.resourcetype),
  );
  const etfTitle =
    etfResourceTypes.size === 1 && etfResourceTypes.has('ETFCommodities')
      ? 'ETF Commodities'
      : 'ETFs';
  const isTradingBlocked = sessionBlocksTrading(session.status);

  return (
    <div className="markets-v2">
      <header className="markets-v2-hero">
        <div className="markets-v2-hero__copy">
          <div className="markets-v2-hero__eyebrow">
            Session 23 · THE WAIT · Day {Math.max(1, session.elapsed_days + 1)}
          </div>
          <h1>Every market. One decision surface.</h1>
          <p>
            Scan the complete configured universe, compare the fields that
            matter to each asset class, and launch a trade without opening a tab
            or drawer.
          </p>
          <div className="markets-v2-hero__stats">
            <div>
              <span>Market value</span>
              <strong>{formatters.million(player.last_market_value)}</strong>
              <small>{reportingCurrencySymbol}</small>
            </div>
            <div>
              <span>Portfolio return</span>
              <strong>
                {formatters.percent2Digit(player.last_portfolio_return)}
              </strong>
            </div>
            <div>
              <span>Available now</span>
              <strong>
                {availableQuotes}
                <small> / {configuredCount}</small>
              </strong>
            </div>
            <div>
              <span>Open positions</span>
              <strong>{activePositions}</strong>
            </div>
          </div>
        </div>

        <div className="markets-v2-hero__desk">
          <div className="markets-v2-hero__topline">
            <span
              className={`markets-v2-session-state ${
                isTradingBlocked ? 'is-blocked' : 'is-running'
              }`}
            >
              <i />
              {SESSION_STATUSES[session.status]}
            </span>
            <ExperienceToggle
              value={experience}
              onChange={onExperienceChange}
              inverse
            />
          </div>
          <div className="markets-v2-cash-desk">
            <div className="markets-v2-cash-desk__head">
              <span>
                <WalletOutlined /> Cash desk
              </span>
              <Button
                size="small"
                icon={<SwapCashIcon />}
                disabled={
                  session.status === PAUSED ||
                  session.status === CREATING ||
                  session.status === FROZEN
                }
                onClick={() => setVisibleExchangeModal(true)}
              >
                Exchange
              </Button>
            </div>
            <div className="markets-v2-wallets">
              {playableWallets.map(wallet => (
                <div key={wallet.id}>
                  <span>{currencies[wallet.currency]?.symbol}</span>
                  <strong>{formatters.commasNoDigits(wallet.amount)}</strong>
                </div>
              ))}
            </div>
          </div>
        </div>
      </header>

      <nav className="markets-v2-nav" aria-label="All markets navigation">
        <div className="markets-v2-nav__links">
          {[
            ['markets-v2-fx', 'FX', groups.fx.length],
            ['markets-v2-etf', etfTitle, groups.etfs.length],
            ['markets-v2-options', 'Options', groups.options.length],
            ['markets-v2-bonds', 'Bonds', groups.bonds.length],
            ['markets-v2-analytics', 'Analytics', null],
          ].map(([id, label, count]) => (
            <button
              type="button"
              key={String(id)}
              onClick={() => scrollToSection(String(id))}
            >
              {label}
              {count !== null && <span>{count}</span>}
            </button>
          ))}
        </div>
        <label className="markets-v2-positions-toggle">
          <span>
            Positions only
            <small>All instruments remain the default</small>
          </span>
          <Switch checked={positionsOnly} onChange={setPositionsOnly} />
        </label>
      </nav>

      <main className="markets-v2-board">
        <div className="markets-v2-board__spot-row">
          <MarketSection
            id="markets-v2-fx"
            eyebrow="Spot"
            title="FX"
            description="Two-way prices, currency exposure and P&L in one scan."
            count={groups.fx.length}
            accent="blue"
          >
            {fxRows.length ? (
              <SpotMarkets
                rows={fxRows}
                kind="fx"
                session={session}
                reportingCurrencySymbol={reportingCurrencySymbol}
                selectedInstrumentId={selectedInstrument?.id}
                selectInstrument={selectInstrument}
                showOrderModal={() => setVisibleOrderModal(true)}
                showODAModal={() => setVisibleODAModal(true)}
              />
            ) : (
              <EmptyPositions />
            )}
          </MarketSection>

          <MarketSection
            id="markets-v2-etf"
            eyebrow="Listed exposure"
            title={etfTitle}
            description="Fund quote, region, position and direct instrument detail."
            count={groups.etfs.length}
            accent="amber"
          >
            {etfRows.length ? (
              <SpotMarkets
                rows={etfRows}
                kind="etf"
                session={session}
                reportingCurrencySymbol={reportingCurrencySymbol}
                selectedInstrumentId={selectedInstrument?.id}
                selectInstrument={selectInstrument}
                showOrderModal={() => setVisibleOrderModal(true)}
                showODAModal={() => setVisibleODAModal(true)}
                showETFDetails={setDetailsAssetId}
              />
            ) : (
              <EmptyPositions />
            )}
          </MarketSection>
        </div>

        <MarketSection
          id="markets-v2-options"
          eyebrow="Defined-risk expressions"
          title="Options"
          description="Strike, volatility, expiry and whole-contract economics stay together."
          count={groups.options.length}
          accent="violet"
        >
          {optionRows.length ? (
            <OptionsMarket
              rows={optionRows}
              session={session}
              currencies={currencies}
              reportingCurrencySymbol={reportingCurrencySymbol}
              orders={orders}
              selectedInstrumentId={selectedInstrument?.id}
              selectInstrument={selectInstrument}
              showOrderModal={() => setVisibleOptionModal(true)}
            />
          ) : (
            <EmptyPositions />
          )}
        </MarketSection>

        <MarketSection
          id="markets-v2-bonds"
          eyebrow="AFS inventory"
          title="Bonds"
          description="Every configured vintage remains visible; future issues activate in place when quoted."
          count={groups.bonds.length}
          accent="green"
          meta={
            <>
              <span>
                Portfolio DV01
                <strong>
                  {formatters.commas2Digits(player.afs_portfolio_dv01)}
                </strong>
              </span>
              <span>
                Duration
                <strong>
                  {formatters.commas2Digits(player.afs_portfolio_duration)}
                </strong>
              </span>
            </>
          }
        >
          {bondRows.length ? (
            <BondsMarket
              rows={bondRows}
              session={session}
              reportingCurrencySymbol={reportingCurrencySymbol}
              selectedInstrumentId={selectedInstrument?.id}
              selectInstrument={selectInstrument}
              showOrderModal={() => setVisibleOrderModal(true)}
              showODAModal={() => setVisibleODAModal(true)}
            />
          ) : (
            <EmptyPositions />
          )}
        </MarketSection>

        {selectedInstrument && traineeContext.instrumentType && (
          <MarketSection
            id="markets-v2-analytics"
            eyebrow="Focused instrument"
            title="Portfolio & market context"
            description="The classic analytics remain connected to whichever instrument you focus."
            count={1}
            accent="blue"
            meta={
              <span>
                Focus
                <strong>
                  {assets[selectedInstrument.underlying_asset]?.ticker}
                </strong>
              </span>
            }
          >
            <div className="markets-v2-analytics">
              <PortfolioOverview instrument={selectedInstrument} />
            </div>
          </MarketSection>
        )}
      </main>

      {visibleOrderModal && selectedInstrument && (
        <OrderModal
          visible={visibleOrderModal}
          hide={() => setVisibleOrderModal(false)}
          id={selectedInstrument.id}
        />
      )}
      {visibleOptionModal && selectedInstrument && (
        <SimplifiedOptionOrderModal
          visible={visibleOptionModal}
          hide={() => setVisibleOptionModal(false)}
          id={selectedInstrument.id}
        />
      )}
      {visibleODAModal && selectedInstrument && (
        <ODAModal
          visible={visibleODAModal}
          hide={() => setVisibleODAModal(false)}
          id={selectedInstrument.id}
        />
      )}
      {visibleExchangeModal && (
        <ExchangeModal
          visible={visibleExchangeModal}
          hide={() => setVisibleExchangeModal(false)}
        />
      )}
      {detailsAssetId !== undefined && (
        <ETFDetailsModal
          assetId={detailsAssetId}
          visible={detailsAssetId !== undefined}
          hide={() => setDetailsAssetId(undefined)}
        />
      )}
    </div>
  );
};

const SwapCashIcon = () => <BankOutlined />;

export default MarketBoard;
